/* Trình phát Learning Experience: học → quiz → thực hành, lưu từng bước. */
import { useEffect, useState, type ReactNode } from "react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Textarea } from "@/components/ui/textarea"
import { Icon, type AnyIconName } from "@/components/icon"
import { Meter, StatusNote } from "@/components/page"
import { post } from "@/lib/api"
import { cn } from "@/lib/utils"

function Block({ icon, title, tone = "soft", children }: { icon: AnyIconName; title: string; tone?: "soft" | "paper"; children: ReactNode }) {
  return (
    <section className={cn("flex gap-4 rounded-2xl p-4 sm:p-5", tone === "soft" ? "bg-soft" : "border-[1.5px] border-line bg-paper")}>
      <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-card">
        <Icon name={icon} className="size-5 text-orange" />
      </span>
      <div className="flex min-w-0 flex-col gap-1.5">
        <h3 className="font-heading text-base font-semibold text-ink">{title}</h3>
        {children}
      </div>
    </section>
  )
}

function CompletedView({ completed, total, onBack }: { completed: any[]; total: number; onBack: () => void }) {
  return (
    <Card className="mx-auto w-full max-w-2xl">
      <CardContent className="flex flex-col items-center gap-4 text-center">
        <img src="/brand/sutucon/mascots-2d/lion-celebrate.svg" alt="" className="size-28" />
        <Badge variant="success">Hoàn thành {completed.length}/{total} bước</Badge>
        <h2 className="text-2xl font-bold">Con đã hoàn thành trải nghiệm này</h2>
        <p className="text-muted-foreground">Mỗi câu trả lời đã được lưu vào Evidence Registry để Simba hiểu cách học của con tốt hơn.</p>
        <Button variant="outline" onClick={onBack}>
          <Icon name="arrow-left" className="size-4" /> Xem lại Package
        </Button>
        {completed.length > 0 && (
          <Collapsible className="w-full text-left">
            <CollapsibleTrigger className="flex w-full items-center justify-between rounded-xl bg-soft px-4 py-3 text-sm font-semibold text-accent-foreground">
              Xem bằng chứng đã lưu <Icon name="chevron-down" className="size-4" />
            </CollapsibleTrigger>
            <CollapsibleContent>
              <ol className="flex flex-col gap-3 pt-3">
                {completed.map((item: any) => (
                  <li key={item.step_id} className="rounded-xl border-[1.5px] border-line p-3 text-sm">
                    <b className="text-ink">Bước {item.step_order}</b>
                    <p className="whitespace-pre-wrap break-words text-muted-foreground">{item.response}</p>
                  </li>
                ))}
              </ol>
            </CollapsibleContent>
          </Collapsible>
        )}
      </CardContent>
    </Card>
  )
}

export function LearningExperiencePlayer({ slug, selected, onBack, onReload }: { slug: string; selected: any; onBack: () => void; onReload: () => Promise<any> }) {
  const completed: any[] = Array.isArray(selected.progress?.completed_steps) ? selected.progress.completed_steps : []
  const completedIds = new Set(completed.map((item) => item.step_id))
  const steps: any[] = Array.isArray(selected.experience?.steps) ? selected.experience.steps : []
  const current = steps.find((step) => !completedIds.has(step.id))
  const [response, setResponse] = useState("")
  const [answerIndex, setAnswerIndex] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState("")
  const [feedback, setFeedback] = useState("")

  useEffect(() => {
    setResponse("")
    setAnswerIndex(null)
    setMessage("")
    setFeedback("")
  }, [current?.id, selected.experience_id])

  const save = async () => {
    if (!current) return
    setBusy(true)
    setMessage("")
    setFeedback("")
    try {
      const result: any = await post(
        `/api/classes/${encodeURIComponent(slug)}/learning-packages/${encodeURIComponent(selected.id)}/steps/${encodeURIComponent(current.id)}`,
        { experience_id: selected.experience_id, step_order: current.order, ...(current.stage === "quiz" ? { answer_index: answerIndex } : { response }) },
      )
      if (current.stage === "quiz" && result?.correct === false) {
        setFeedback(result.feedback || "Chưa đúng. Con xem lại ví dụ rồi thử lại nhé.")
        return
      }
      await onReload()
      window.scrollTo({ top: 0, behavior: "smooth" })
    } catch (e: any) {
      setMessage(e.message)
    } finally {
      setBusy(false)
    }
  }

  if (!current) return <CompletedView completed={completed} total={steps.length} onBack={onBack} />

  const stage = String(current.stage || "practice")
  const quizSteps = steps.filter((step) => step.stage === "quiz")
  const quizIds = new Set(quizSteps.map((step) => step.id))
  const quizCompleted = completed.filter((item) => quizIds.has(item.step_id)).length
  const quizIndex = Math.max(0, quizSteps.findIndex((step) => step.id === current.id))
  const progress =
    stage === "quiz"
      ? Math.round((quizCompleted / Math.max(quizSteps.length, 1)) * 100)
      : Math.round((completed.length / Math.max(steps.length, 1)) * 100)
  const canContinue = stage === "learn" ? true : stage === "quiz" ? answerIndex !== null : response.trim().length >= 20
  const cta = busy
    ? "Đang lưu…"
    : stage === "learn"
      ? "Con đã hiểu · Làm quiz"
      : stage === "quiz"
        ? "Kiểm tra đáp án"
        : current.order === steps.length
          ? "Hoàn thành trải nghiệm"
          : "Lưu và sang bước tiếp"

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-5">
      <div className="flex items-center justify-between gap-3">
        <Button variant="outline" size="sm" onClick={onBack}>
          <Icon name="arrow-left" className="size-4" /> Thoát bài học
        </Button>
        <span className="font-heading text-lg font-bold text-orange-strong">{progress}%</span>
      </div>
      <Card>
        <CardContent className="flex flex-col gap-5">
          <header className="flex flex-col gap-3">
            <p className="text-sm font-bold tracking-[0.08em] text-orange uppercase">
              {stage === "quiz" ? `Quiz bắt buộc · Câu ${quizIndex + 1}/${quizSteps.length}` : `Learning Experience · Bước ${current.order}/${steps.length}`}
            </p>
            <h2 className="text-2xl leading-tight font-bold">{current.title}</h2>
            <div
              aria-label={stage === "quiz" ? `Đã hoàn thành ${quizCompleted} trên ${quizSteps.length} câu quiz` : `Đã hoàn thành ${completed.length} trên ${steps.length} bước`}
            >
              <Meter value={progress} />
            </div>
          </header>

          {stage === "learn" && (
            <>
              <Block icon="book-open" title="Giải thích dễ hiểu">
                <p className="text-base text-ink">{current.instruction}</p>
                {current.support && <p className="text-sm text-muted-foreground">{current.support}</p>}
              </Block>
              <Block icon="lightbulb" title="Ví dụ gần gũi" tone="paper">
                <p className="text-base text-ink">{current.example}</p>
              </Block>
            </>
          )}

          {stage === "quiz" && (
            <section className="flex flex-col gap-4">
              <p className="text-lg font-semibold text-ink">{current.quiz?.question}</p>
              <RadioGroup
                aria-label="Chọn một đáp án"
                value={answerIndex === null ? null : String(answerIndex)}
                onValueChange={(v) => {
                  setAnswerIndex(Number(v))
                  setFeedback("")
                }}
                className="gap-3"
              >
                {(current.quiz?.options || []).map((option: string, index: number) => (
                  <label
                    key={option}
                    className={cn(
                      "flex cursor-pointer items-center gap-3 rounded-2xl border-[1.5px] px-4 py-3.5 text-base transition-colors",
                      answerIndex === index ? "border-orange bg-soft text-ink" : "border-line bg-card hover:border-edge",
                    )}
                  >
                    <RadioGroupItem value={String(index)} />
                    <span className="min-w-0 break-words">{option}</span>
                  </label>
                ))}
              </RadioGroup>
              {feedback && (
                <StatusNote tone="error" className="flex items-start gap-2">
                  <Icon name="lightbulb" className="size-4" /> {feedback}
                </StatusNote>
              )}
              {current.support && <p className="text-sm text-muted-foreground">{current.support}</p>}
              <p className="text-sm text-muted-foreground">
                Con cần hoàn thành đủ {quizSteps.length} câu quiz trước khi mở bài thực hành. Đã xong: {quizCompleted}/{quizSteps.length}.
              </p>
            </section>
          )}

          {stage === "practice" && (
            <>
              <Block icon="book-open" title="Bài thực hành nhỏ">
                <p className="text-base text-ink">{current.instruction}</p>
                {current.support && <p className="text-sm text-muted-foreground">{current.support}</p>}
              </Block>
              <Block icon="file" title="Con cần nộp gì?" tone="paper">
                <p className="text-base text-ink">{current.evidence}</p>
              </Block>
              <label className="flex flex-col gap-2">
                <span className="text-sm font-semibold text-ink">Câu trả lời của con</span>
                <Textarea
                  value={response}
                  maxLength={2000}
                  rows={6}
                  onChange={(e) => setResponse(e.target.value)}
                  placeholder="Viết ngắn gọn bằng lời của con…"
                  className="text-base"
                />
                <span className="text-sm text-muted-foreground">{response.trim().length}/2000 ký tự · tối thiểu 20 ký tự</span>
              </label>
            </>
          )}

          <StatusNote tone="error">{message}</StatusNote>

          <footer className="flex flex-wrap items-center justify-between gap-3 border-t-[1.5px] border-line pt-5">
            <span className="flex items-center gap-2 text-sm text-muted-foreground">
              <Icon name="clock" className="size-4" /> {current.duration_minutes} phút
            </span>
            <Button size="lg" disabled={busy || !canContinue} onClick={save}>
              {cta} <Icon name="arrow-right" className="size-4" />
            </Button>
          </footer>
        </CardContent>
      </Card>
    </div>
  )
}
